import React, { useMemo, useState } from "react";
import TabsHeader from "../../components/TabsHeader";
import { AddUserIcon, BackArrowIcon, NotIcon, PhoneIcon, PlusIcon, SearchIcon, trophyIcon } from "../../assets/icons";

const HelpPage = () => {
    const [activeTab, setActiveTab] = useState("All Topics");
    const [query, setQuery] = useState("");

    const tabs = useMemo(
        () => [
            { name: "All Topics" },
            { name: "Getting started" },
            { name: "Billing & Payments" },
            { name: "Support procedures" },
            { name: "Technical Set up" },
            { name: "Security" },
        ],
        []
    );

    const faqData = useMemo(
        () => ({
            "Getting started": [
                {
                    q: "How do I provision a new tenant?",
                    a:
                        'Go to Tenants > "Add Tenant" and complete company info, plan, and modules. Review and confirm to provision.',
                },
                {
                    q: "How do I invite staff to the command center?",
                    a:
                        'Navigate to Staff > "Add Staff", fill details and role, then send invite. The user sets their password from the email.',
                },
            ],
            "Billing & Payments": [
                {
                    q: "How do I handle failed payments?",
                    a:
                        "Open Billing > Dunning Management, set retry schedule, and notify the tenant. Update payment method from the tenant profile if needed.",
                },
                {
                    q: "What is the difference between soft lock and hard delete?",
                    a:
                        "Soft lock suspends access but preserves data and invoices. Hard delete permanently removes tenant data after retention policies.",
                },
            ],
            "Support procedures": [
                {
                    q: "How do I impersonate a tenant for support?",
                    a:
                        'From Tenants > Actions > "Access account". A time-limited token is generated and audited in Logs.',
                },
                {
                    q: "How do I escalate tickets to engineering?",
                    a:
                        "Create a feedback triage item linking the tenant, attach logs, and add priority and reproduction steps.",
                },
            ],
            "Technical Set up": [
                {
                    q: "How do I enable or disable modules for a tenant?",
                    a:
                        'Go to Tenants > Select tenant > Modules and toggle features. Changes apply after you click "Save".',
                },
                {
                    q: "What are API Rate limits?",
                    a:
                        "Standard tenants have 600 requests/min burst with sliding window; enterprise plans can be customized per contract.",
                },
            ],
            "Security": [
                {
                    q: "How do I handle a security incident?",
                    a:
                        "Immediately rotate credentials, enable sandbox mode if needed, notify stakeholders, and file a report under Quick Actions.",
                },
                {
                    q: "How do I set up SSO for admins?",
                    a:
                        "In Settings > Security, configure your IdP (SAML/OIDC), upload metadata, and assign roles. Test in sandbox first.",
                },
            ],
        }),
        []
    );

    const allFaqs = useMemo(() => Object.values(faqData).flat(), [faqData]);

    const visibleFaqs = useMemo(() => {
        const selected = activeTab === "All Topics" ? allFaqs : faqData[activeTab] || [];
        if (!query.trim()) return selected;
        const qLower = query.toLowerCase();
        return selected.filter(
            (item) => item.q.toLowerCase().includes(qLower) || item.a.toLowerCase().includes(qLower)
        );
    }, [activeTab, allFaqs, faqData, query]);

    return (
        <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-4 md:space-y-6 pb-6 h-full overflow-y-auto">
            <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
                <div className="mb-4">
                    <button
                        type="button"
                        className="text-[16px] font-normal flex gap-1"
                    >
                        {BackArrowIcon} Back
                    </button>
                </div>
                <div className="heading flex gap-2 justify-between items-end">
                    <div className="heading-title space-y-1">
                        <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
                            Help & Documentation
                        </h1>
                        <p className="text-span text-dark mb-0">
                            Find answers and learn how to use the command center
                        </p>
                    </div>
                </div>
            </div>
            <div className='px-3 sm:px-6'>
                <div className="w-full mb-4">
                    <div className="bg-[#F7F7F7] rounded-lg px-4 py-3 flex items-center gap-2">
                        <span className="flex-none">{SearchIcon}</span>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search"
                            className="w-full bg-transparent outline-none text-body placeholder:text-body text-[#787891] placeholder:text-[#787891]"
                        />
                    </div>
                </div>
                <TabsHeader tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} className="help-tabs" />
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <div className="chart-card space-y-6 md:col-span-2 xl:col-span-3">
                        <h2 className="text-span text-black !font-semibold mb-0">Frequently asked questions</h2>
                        <ul className="space-y-5">
                            {visibleFaqs.map((item, idx) => (
                                <li key={idx} className="pb-5 border-b-[1px] border-[#F3EFEF]">
                                    <div className="text-span !font-medium text-black">{item.q}</div>
                                    <p className="mb-0 text-sub_span text-[#6B7280]">{item.a}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="chart-card space-y-5">
                        <h2 className="text-span text-black !font-semibold mb-0">Quick Resources </h2>
                        <div className="space-y-4">
                            <div className="flex flex-col gap-3">
                                <p className="text-sub_span text-black !font-semibold mb-0">Command center guide </p>
                                <p className="text-sub_span text-[#888076]">Complete guide to using the command center </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <p className="text-sub_span text-black !font-semibold mb-0">Video Tutorial </p>
                                <p className="text-sub_span text-[#888076]">Step-by step video walkthrough </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <p className="text-sub_span text-black !font-semibold mb-0">API documentation  </p>
                                <p className="text-sub_span text-[#888076]">Technical API refrences and example  </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <p className="text-sub_span text-black !font-semibold mb-0">Best Practices  </p>
                                <p className="text-sub_span text-[#888076]">Recommended workflows and procedures  </p>
                            </div>
                        </div>
                    </div>
                    <div className="chart-card space-y-5">
                        <h2 className="text-span text-black !font-semibold mb-0">Need More help  </h2>
                        <div className="space-y-4">
                            <div className="bg-[#F6FFEC] px-4 py-5 rounded-md flex gap-3">
                                <div className={`w-[40px] h-[40px] flex-none comman-svg flex items-center justify-center rounded-full text-white bg-[#52C41A]`}>
                                    <span className="flex-none">{NotIcon}</span>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-span text-black !font-medium mb-0">Support hours </p>
                                    <p className="text-span text-[#888076] mb-0">Monday -Friday  </p>
                                    <p className="text-span text-[#888076] mb-0">9:00AM-6:00PM PST </p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex flex-col gap-3">
                                    <p className="text-sub_span text-black !font-semibold mb-0">Phone Support  </p>
                                    <p className="text-sub_span text-[#888076]">+555-8668-967 </p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <p className="text-sub_span text-black !font-semibold mb-0">Email  Support   </p>
                                    <p className="text-sub_span text-[#888076]">monvetahr@gmail.com</p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    <p className="text-sub_span text-black !font-semibold mb-0">Live chat   </p>
                                    <p className="text-sub_span text-[#888076]">Start live chat </p>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="chart-card space-y-5 md:col-span-2 xl:col-span-1">
                        <h2 className="text-span text-black !font-semibold mb-0">Quick Actions</h2>
                        <div className="space-y-4">
                            <div
                                className="bg-[#FFEAD4] p-4 py-5 rounded-lg font-medium flex flex-col items-center justify-center gap-2 text-[16px] font-semibold text-[#000]"
                            >
                                {PlusIcon} Submit feature request 
                            </div>
                           
                            <div className="bg-[#D0FFDD] p-4 py-5 rounded-lg font-medium flex flex-col items-center justify-center gap-2 text-[16px] font-semibold text-[#000]">
                                {trophyIcon} Report security issues 
                            </div>
                            <div className="bg-[#D6E3FF] p-4 py-5 rounded-lg font-medium flex flex-col items-center justify-center gap-2 text-[16px] font-semibold text-[#000]">
                                {PhoneIcon} Download user Manual 
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpPage;


